/**
 * Shared Cash Position Summary grid/filter types (DCP-145 / DCP-144).
 *
 * The column model is produced ENTIRELY server-side by the Cash Position RPCs
 * (facility / pack-subtotal / total rows, pre-ordered) and mapped 1:1 by the
 * consuming hook. This file holds only the filter selection, its transitions
 * and the Combined Total gate — no client-side aggregation remains.
 */

import type {
  CashPositionMultiPeriodColumn,
  CashPositionQueryFilters,
} from '@/hooks/useCashPositionSummaryMultiPeriod'
import {
  facilitiesForPackSelection,
  NO_PACK,
  toFilterEntries,
} from '@/components/ledgersync/connectionManagement.utils'

/**
 * Cash Position Summary filter selection. The pack selection sets the scope;
 * the facility part means one thing per mode, so an empty list is never
 * ambiguous:
 *   - no pack selected — `facilityIds` are the picked facilities (empty = all);
 *   - packs selected — `excludedFacilityIds` are the members left out (empty =
 *     every facility of those packs).
 * The field of the other mode is always empty.
 */
export interface CashPositionFilterState {
  /** Selected pack ids; the `NO_PACK` sentinel stands for facilities without a pack. */
  packIds: string[]
  /** Facilities picked while no pack is selected. */
  facilityIds: string[]
  /** Facilities of the selected packs left out of the report. */
  excludedFacilityIds: string[]
}

/** The unfiltered report. */
export const NO_CASH_POSITION_FILTERS: CashPositionFilterState = {
  packIds: [],
  facilityIds: [],
  excludedFacilityIds: [],
}

type ActiveFacility = { id: string; pack_id: string | null }

const scopeOf = (activeFacilities: ActiveFacility[], packIds: string[]) =>
  facilitiesForPackSelection(activeFacilities, packIds).map((f) => f.id)

/**
 * The facilities the Facilities filter shows ticked: the picked ones with no
 * pack selected, otherwise every facility of the selected packs not left out.
 *
 * @param filters - the current selection
 * @param activeFacilities - the org's active facilities with their pack
 * @returns facility ids, in `activeFacilities` order once packs are selected
 */
export function tickedFacilityIds(
  filters: CashPositionFilterState,
  activeFacilities: ActiveFacility[],
): string[] {
  if (filters.packIds.length === 0) return filters.facilityIds
  return scopeOf(activeFacilities, filters.packIds).filter((id) => !filters.excludedFacilityIds.includes(id))
}

/**
 * The selection after Packs changes. An added pack comes in with all of its
 * facilities; a removed one takes its left-out facilities with it. Facilities
 * picked before any pack was selected are dropped: the scope now decides.
 *
 * @param filters - the current selection
 * @param packIds - the new pack selection
 * @param activeFacilities - the org's active facilities with their pack
 * @returns the selection to apply
 */
export function selectCashPositionPacks(
  filters: CashPositionFilterState,
  packIds: string[],
  activeFacilities: ActiveFacility[],
): CashPositionFilterState {
  if (packIds.length === 0) return NO_CASH_POSITION_FILTERS
  const scope = new Set(scopeOf(activeFacilities, packIds))
  return {
    packIds,
    facilityIds: [],
    excludedFacilityIds: filters.excludedFacilityIds.filter((id) => scope.has(id)),
  }
}

/**
 * The selection after Facilities changes to `ticked`. With packs selected the
 * unticked members are recorded as left out; unticking every one of them would
 * leave nothing to report, so it reads as "all of them" again.
 *
 * @param filters - the current selection
 * @param ticked - the facilities now ticked in the Facilities filter
 * @param activeFacilities - the org's active facilities with their pack
 * @returns the selection to apply
 */
export function selectCashPositionFacilities(
  filters: CashPositionFilterState,
  ticked: string[],
  activeFacilities: ActiveFacility[],
): CashPositionFilterState {
  if (filters.packIds.length === 0) return { ...filters, facilityIds: ticked }
  const scope = scopeOf(activeFacilities, filters.packIds)
  const excluded = scope.filter((id) => !ticked.includes(id))
  return { ...filters, excludedFacilityIds: excluded.length === scope.length ? [] : excluded }
}

/**
 * The RPC filters for a selection. Facility ids go out only when they narrow
 * something: with nothing left out the server expands the packs itself, so the
 * report never depends on the client's copy of the pack membership.
 *
 * @param filters - the current selection
 * @param activeFacilities - the org's active facilities with their pack
 * @returns the summary hook's filters ("No Pack" as a null entry)
 */
export function cashPositionQueryFilters(
  filters: CashPositionFilterState,
  activeFacilities: ActiveFacility[],
): CashPositionQueryFilters {
  const packIds = toFilterEntries(filters.packIds, NO_PACK)
  const narrowed = filters.packIds.length === 0 || filters.excludedFacilityIds.length > 0
  return { packIds, facilityIds: narrowed ? tickedFacilityIds(filters, activeFacilities) : [] }
}

/**
 * Whether the frozen "Combined Total" column is worth rendering for a filtered
 * report (DCP-293; the unfiltered one always shows it): not when it would copy
 * a column already on screen —
 *   - a single facility column, which IS the whole scope;
 *   - one pack subtotal with nothing beside its members, which already sums
 *     every facility shown.
 *
 * Read off the columns the server returned rather than the filter ids: whether
 * a pack gets a subtotal depends on whether all of its members are shown, which
 * only the server knows. It also keeps the gate in step with the grid while a
 * refetch still shows the previous result.
 *
 * @param columns - the report's facility and pack-subtotal columns
 * @returns true when the Combined Total column should be rendered
 */
export function shouldShowCombinedTotal(columns: CashPositionMultiPeriodColumn[]): boolean {
  const facilities = columns.filter((c) => c.kind === 'facility')
  const packs = columns.filter((c) => c.kind === 'pack')
  if (facilities.length === 1) return false
  const onePackOnly = packs.length === 1 && facilities.every((f) => f.packId === packs[0].packId)
  return !onePackOnly
}
